import { type LicenseStatus } from "@clipboard-health/contract-worker-app-bff";
import { formatDate } from "@clipboard-health/date-time";
import { Card, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, Stack } from "@mui/material";

import { Button } from "../components/Button";
import { LicenseStatusPill } from "./LicenseStatusPill";
import { LicenseStateText } from "./StateText";
import { useShowRenewLicenseInstructions } from "./useShowRenewLicenseInstructions";

interface LicenseCardProps {
  state: string;
  nlcStates: Set<string>;
  multiState: boolean;
  status: LicenseStatus;
  expiresAt?: string;
  number?: string;
  licenseQualification: string;
  onRemove: () => void;
  onRenew: () => void;
  onNlcStatesClick: () => void;
}

export function LicenseCard(props: LicenseCardProps) {
  const {
    state,
    nlcStates,
    multiState,
    status,
    expiresAt,
    number,
    onRemove,
    onNlcStatesClick,
    onRenew,
    licenseQualification,
  } = props;

  const shouldShowRenewLicenseInstructions = useShowRenewLicenseInstructions({
    licenseAttributes: {
      status,
      qualification: licenseQualification,
      expiresAt,
    },
  });

  return (
    <Card outlined variant="quaternary" sx={{ flexShrink: 0 }}>
      <CardContent>
        <Stack
          sx={{
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
            gap: 3,
          }}
        >
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <LicenseStateText
              state={state}
              nlcStates={nlcStates}
              multiState={multiState}
              onNlcStatesClick={onNlcStatesClick}
            />

            <LicenseStatusPill status={status} />
          </Stack>

          {Boolean(number) && (
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              #{number}
            </Text>
          )}

          {(status === "ACTIVE" || status === "EXPIRING" || status === "EXPIRED") && (
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {isDefined(expiresAt)
                ? `${status === "EXPIRED" ? "Expired" : "Expiring"} on ${formatDate(expiresAt)}`
                : "Valid indefinitely"}
            </Text>
          )}

          <Stack gap={2} sx={{ marginTop: 4 }}>
            {shouldShowRenewLicenseInstructions && (
              <>
                <Button fullWidth variant="outlined" size="small" onClick={onRenew}>
                  Renew this license
                </Button>
                <Text
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.secondary, textAlign: "center" })}
                >
                  or
                </Text>
              </>
            )}

            <Button fullWidth variant="outlined" size="small" onClick={onRemove}>
              Remove this license
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
